import React from 'react';
import { Box, Grid, Paper, Typography, List, ListItem, ListItemText, ListItemIcon, Button, Divider, Avatar, Skeleton, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TimerIcon from '@mui/icons-material/Timer';
import ReceiptIcon from '@mui/icons-material/Receipt';
import TimelineIcon from '@mui/icons-material/Timeline';
import EventNoteIcon from '@mui/icons-material/EventNote';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CircleIcon from '@mui/icons-material/Circle';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid } from 'recharts';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PaymentIcon from '@mui/icons-material/Payment';
import RequestPageIcon from '@mui/icons-material/RequestPage';
import CloseIcon from '@mui/icons-material/Close';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const summaryData = {
    totalOutstandingLoans: '15,750,000 SAR',
    upcomingPayments: '2,500,000 SAR',
    availableHeadroom: '7,250,000 SAR',
    loansNearExpiry: 3
  };

  const quickNavigation = [
    { title: 'New Loan', icon: <ReceiptIcon />, path: '/transactions/loan' },
    { title: 'Loan Utilization', icon: <TimelineIcon />, path: '/transactions/loan-utilization' },
    { title: 'Repayment Schedule', icon: <EventNoteIcon />, path: '/transactions/repayment' },
    { title: 'Bank Management', icon: <AccountBalanceIcon />, path: '/parameters/bank-management' }
  ];

  const recentActivities = [
    { 
      type: 'Loan Disbursement',
      description: 'Loan #L123 disbursed to ABC Corp',
      amount: '5,000,000 SAR',
      timestamp: '2 hours ago',
      status: 'success'
    },
    {
      type: 'Payment Received',
      description: 'Payment received for Loan #L120',
      amount: '750,000 SAR',
      timestamp: '5 hours ago',
      status: 'success'
    },
    {
      type: 'Loan Request',
      description: 'New loan request from XYZ Ltd',
      amount: '3,000,000 SAR',
      timestamp: '1 day ago',
      status: 'pending'
    },
    {
      type: 'Payment Due',
      description: 'Payment due for Loan #L118',
      amount: '1,200,000 SAR',
      timestamp: '2 days ago',
      status: 'warning'
    }
  ];

  // Enhanced chart data
  const loanTrendsData = [
    { month: 'Jan', disbursed: 3000000, repaid: 1200000 },
    { month: 'Feb', disbursed: 5000000, repaid: 2000000 },
    { month: 'Mar', disbursed: 2500000, repaid: 1800000 },
    { month: 'Apr', disbursed: 6000000, repaid: 2500000 },
    { month: 'May', disbursed: 4000000, repaid: 2200000 },
    { month: 'Jun', disbursed: 7000000, repaid: 3000000 },
  ];

  const paymentStatusBarData = [
    { status: 'Paid', count: 8 },
    { status: 'Pending', count: 3 },
    { status: 'Overdue', count: 2 },
  ];

  const COLORS = ['#10B981', '#6366F1', '#F43F5E'];

  const [activityLoading, setActivityLoading] = React.useState(true);
  const [viewAllOpen, setViewAllOpen] = React.useState(false);
  React.useEffect(() => {
    const timer = setTimeout(() => setActivityLoading(false), 1200);
    return () => clearTimeout(timer);
  }, []);
  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'Loan Disbursement': return <ReceiptIcon sx={{ color: '#6366F1' }} />;
      case 'Payment Received': return <PaymentIcon sx={{ color: '#10B981' }} />;
      case 'Loan Request': return <RequestPageIcon sx={{ color: '#F59E0B' }} />;
      case 'Payment Due': return <EventNoteIcon sx={{ color: '#F43F5E' }} />;
      default: return <Avatar sx={{ bgcolor: '#E5E7EB', width: 32, height: 32 }} />;
    }
  };
  const getStatusDot = (status: string) => {
    switch (status) {
      case 'success': return <Box sx={{ width: 10, height: 10, bgcolor: '#10B981', borderRadius: '50%', mr: 1 }} />;
      case 'pending': return <Box sx={{ width: 10, height: 10, bgcolor: '#6366F1', borderRadius: '50%', mr: 1 }} />;
      case 'warning': return <Box sx={{ width: 10, height: 10, bgcolor: '#F59E0B', borderRadius: '50%', mr: 1 }} />;
      default: return <Box sx={{ width: 10, height: 10, bgcolor: '#F43F5E', borderRadius: '50%', mr: 1 }} />;
    }
  };

  const StatCard = ({ title, value, icon, color = '#6366F1' }: { title: string; value: string | number; icon: React.ReactNode; color?: string }) => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: '100%',
        borderRadius: 3,
        bgcolor: 'linear-gradient(135deg, #F9FAFB 60%, #F3F4F6 100%)',
        border: '1px solid #E5E7EB',
        transition: 'all 0.2s',
        boxShadow: '0 2px 8px 0 rgba(99,102,241,0.06)',
        '&:hover': {
          transform: 'translateY(-4px) scale(1.03)',
          boxShadow: '0 8px 24px 0 rgba(99,102,241,0.12)',
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography component="h2" sx={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: 500, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {title}
            <InfoOutlinedIcon sx={{ fontSize: 18, color: '#6366F1' }} titleAccess={`Info about ${title}`} />
          </Typography>
          <Typography component="p" sx={{ color: '#111827', fontSize: '1.5rem', fontWeight: 'bold', letterSpacing: '-0.025em' }}>
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            p: 2,
            borderRadius: '50%',
            bgcolor: `${color}20`,
            color: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 2px 8px 0 ${color}22`
          }}
        >
          {React.cloneElement(icon as React.ReactElement, { sx: { fontSize: 48 } })}
        </Box>
      </Box>
    </Paper>
  );

  const QuickNavCard = ({ title, icon, onClick, bgcolor = '#FFFFFF', highlight = false }: { title: string; icon: React.ReactNode; onClick: () => void; bgcolor?: string; highlight?: boolean }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: '100%',
        borderRadius: 2,
        bgcolor: bgcolor,
        border: '1px solid #E5E7EB',
        cursor: 'pointer',
        transition: 'all 0.2s',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: '0 4px 16px 0 #6366F122',
          bgcolor: highlight ? '#E0E7FF' : '#F3F4F6',
          transform: 'translateY(-2px) scale(1.03)',
        },
        '&:active': {
          boxShadow: '0 2px 8px 0 #6366F122',
          transform: 'scale(0.98)',
        },
      }}
      onClick={onClick}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            p: 2,
            borderRadius: '50%',
            bgcolor: '#fff',
            color: '#6366F1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px 0 #6366F122',
            position: 'relative',
          }}
        >
          {highlight && (
            <Box sx={{ position: 'absolute', top: 2, right: 2, bgcolor: '#6366F1', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 16, lineHeight: 1 }}>+</Typography>
            </Box>
          )}
          {React.cloneElement(icon as React.ReactElement, { sx: { fontSize: 44 } })}
        </Box>
        <Typography 
          sx={{ 
            color: '#374151',
            fontSize: '0.875rem',
            fontWeight: 500,
            textAlign: 'center'
          }}
        >
          {title}
        </Typography>
      </Box>
    </Paper>
  );

  return (
    <Box>
      <Typography 
        variant="h4" 
        sx={{ 
          mb: 4,
          color: '#111827',
          fontWeight: 'bold',
          letterSpacing: '-0.025em'
        }}
      >
        Dashboard
      </Typography>

      {/* Summary Widgets */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Total Outstanding Loans" 
            value={summaryData.totalOutstandingLoans} 
            icon={<AccountBalanceWalletIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Upcoming Payments (7 Days)" 
            value={summaryData.upcomingPayments} 
            icon={<PaymentsIcon />}
            color="#10B981"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Available Headroom" 
            value={summaryData.availableHeadroom} 
            icon={<TrendingUpIcon />}
            color="#3B82F6"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Loans Near Expiry" 
            value={summaryData.loansNearExpiry} 
            icon={<TimerIcon />}
            color="#F43F5E"
          />
        </Grid>
      </Grid>

      {/* Quick Navigation */}
      <Box sx={{ px: 1 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 3,
            mt: 8,
            color: '#111827',
            fontWeight: 'bold'
          }}
        >
          Quick Actions
        </Typography>
      </Box>
      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid item xs={6} sm={3}>
          <QuickNavCard
            title="New Loan"
            icon={<ReceiptIcon />}
            onClick={() => navigate('/transactions/loan')}
            bgcolor="#EEF2FF"
            highlight
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <QuickNavCard
            title="Loan Utilization"
            icon={<TimelineIcon />}
            onClick={() => navigate('/transactions/loan-utilization')}
            bgcolor="#ECFDF5"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <QuickNavCard
            title="Repayment Schedule"
            icon={<EventNoteIcon />}
            onClick={() => navigate('/transactions/repayment')}
            bgcolor="#FEF9C3"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <QuickNavCard
            title="Bank Management"
            icon={<AccountBalanceIcon />}
            onClick={() => navigate('/parameters/bank-management')}
            bgcolor="#E0F2FE"
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mt: 6 }}>
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: '#FFFFFF',
              border: '1px solid #E5E7EB',
              height: '100%'
            }}
          >
            <Typography variant="h6" sx={{ mb: 3, color: '#111827', fontWeight: 'bold' }}>
              Loan Disbursement & Repayment Trends
            </Typography>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={loanTrendsData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={v => `${(v/1_000_000)}M`} />
                <Tooltip formatter={v => `${v.toLocaleString()} SAR`} />
                <Legend />
                <Line type="monotone" dataKey="disbursed" name="Loans Disbursed" stroke="#6366F1" strokeWidth={3} dot={{ r: 5 }} />
                <Line type="monotone" dataKey="repaid" name="Repayments" stroke="#10B981" strokeWidth={3} dot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: '#FFFFFF',
              border: '1px solid #E5E7EB',
              height: '100%'
            }}
          >
            <Typography variant="h6" sx={{ mb: 3, color: '#111827', fontWeight: 'bold' }}>
              Payment Status (Current Month)
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={paymentStatusBarData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="status" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" name="Number of Payments" fill="#6366F1">
                  {paymentStatusBarData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Activity */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ mt: 10, mb: 2, color: '#374151', fontWeight: 700 }}>
          Recent Activity
        </Typography>
        <Button size="small" sx={{ color: '#6366F1', fontWeight: 600, textTransform: 'none', mt: 10 }} onClick={() => setViewAllOpen(true)}>
          View All
        </Button>
      </Box>
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: '#fff', border: '1px solid #E5E7EB', mb: 4 }}>
        {activityLoading ? (
          <List>
            {[...Array(4)].map((_, idx) => (
              <ListItem key={idx} disablePadding sx={{ mb: 1 }}>
                <ListItemIcon>
                  <Skeleton variant="circular" width={32} height={32} />
                </ListItemIcon>
                <ListItemText
                  primary={<Skeleton width="60%" />}
                  secondary={<Skeleton width="40%" />}
                />
                <Box sx={{ textAlign: 'right' }}>
                  <Skeleton width={60} />
                  <Skeleton width={40} />
                </Box>
              </ListItem>
            ))}
          </List>
        ) : (
          <List>
            {recentActivities.map((activity, idx) => (
              <ListItem key={idx} disablePadding sx={{ mb: 1 }}>
                <ListItemIcon>
                  <Avatar sx={{ bgcolor: '#F3F4F6', width: 32, height: 32 }}>
                    {getActivityIcon(activity.type)}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={<Box sx={{ display: 'flex', alignItems: 'center' }}>{getStatusDot(activity.status)}<Typography sx={{ fontWeight: 500 }}>{activity.type}</Typography></Box>}
                  secondary={<Typography sx={{ color: '#6B7280', fontSize: '0.95rem' }}>{activity.description}</Typography>}
                />
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.85rem' }}>{activity.timestamp}</Typography>
                  <Typography sx={{ color: '#374151', fontWeight: 600 }}>{activity.amount}</Typography>
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
      <Dialog open={viewAllOpen} onClose={() => setViewAllOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          All Recent Activities
          <IconButton onClick={() => setViewAllOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <List>
            {recentActivities.map((activity, idx) => (
              <ListItem key={idx} disablePadding sx={{ mb: 1 }}>
                <ListItemIcon>
                  <Avatar sx={{ bgcolor: '#F3F4F6', width: 32, height: 32 }}>
                    {getActivityIcon(activity.type)}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={<Box sx={{ display: 'flex', alignItems: 'center' }}>{getStatusDot(activity.status)}<Typography sx={{ fontWeight: 500 }}>{activity.type}</Typography></Box>}
                  secondary={<Typography sx={{ color: '#6B7280', fontSize: '0.95rem' }}>{activity.description}</Typography>}
                />
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.85rem' }}>{activity.timestamp}</Typography>
                  <Typography sx={{ color: '#374151', fontWeight: 600 }}>{activity.amount}</Typography>
                </Box>
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Dashboard; 